/**
 * VariantPodCell — cellule POD pour une variante (S5.6 PR3).
 *
 * Affiche, dans la colonne dédiée de `<VariantsPanel>` :
 *   - un `<PodStatusBadge>` quand une row POD active existe (status
 *     `draft|uploading|ready|failed`)
 *   - un bouton "Préparer pour POD" si pas encore préparée
 *   - un menu d'actions secondaires (Refaire / Archiver) si déjà ready
 *     ou failed
 *
 * Le polling 5s vit dans `useVariantPodStatus` (refetchInterval
 * conditionnel) — ce composant ne gère que le rendu + les mutations.
 *
 * SKU Gelato par défaut : la convention atelier est `GLOBAL-CFP-A4`
 * (Classic Fine Art Print A4) en attendant une UI de mapping plus fine
 * (champ libre ou dropdown des SKUs Gelato par taille variant). Cette
 * UI minimale permet déjà à Stéphanie de cliquer "Préparer" et obtenir
 * un produit POD valide pour ses A4 — les autres tailles passeront par
 * un select SKU à livrer après les premiers retours utilisatrice.
 */

import { useMemo } from 'react'
import { Loader2, Sparkles, Trash2 } from 'lucide-react'
import { toast } from 'sonner'

import Button from '@/components/ui/Button'
import { useConfirm } from '@/contexts/confirm-dialog-context'
import { useArchiveVariantPod, usePrepareVariantForPod, useVariantPodStatus } from '@/hooks/usePod'
import type { PodProvider } from '@/types/pod'

import PodStatusBadge from './PodStatusBadge'

import './VariantPodCell.css'

interface VariantPodCellProps {
  variantId: number
  /** Label commercial humain pour les modales de confirmation. */
  variantLabel: string
  /** Slug provider à utiliser pour la préparation. Gelato par défaut
   * tant que le user n'a connecté qu'un seul provider. */
  defaultProvider?: PodProvider
  // Refonte POD v6.1 : `defaultSku` retiré — le SKU est porté par
  // `work_variants.printable_spec_id` côté DB. La préparation POD ne
  // prend plus de SKU en payload.
  // Note (2026-05-15) : le libellé du preset POD lié vit côté
  // `<VariantsPanel>` dans la cellule Composition — pas ici. Cette
  // cellule reste compacte (badge OU bouton).
}

const DEFAULT_PROVIDER: PodProvider = 'gelato'

export default function VariantPodCell({
  variantId,
  variantLabel,
  defaultProvider = DEFAULT_PROVIDER,
}: VariantPodCellProps) {
  const statusQuery = useVariantPodStatus(variantId)
  const prepareMutation = usePrepareVariantForPod()
  const archiveMutation = useArchiveVariantPod()
  const { confirm } = useConfirm()

  const podRow = statusQuery.data ?? null

  const canPrepare = useMemo(() => {
    if (!podRow) return true
    return podRow.status === 'failed'
  }, [podRow])

  const handlePrepare = async () => {
    const confirmed = await confirm({
      title: `Préparer « ${variantLabel} » chez Gelato ?`,
      message:
        'Le SKU sera validé chez le provider et un coût indicatif sera récupéré. Préparation typique 30-60s.',
      confirmText: 'Préparer',
    })
    if (!confirmed) return
    try {
      // Refonte POD v6.1 : `production_specs` retiré du payload — la
      // variante doit être pré-bindée à un `printable_spec` (cf.
      // `<VariantModal>`). Le BE résout sku + attributes depuis la spec.
      await prepareMutation.mutateAsync({
        variantId,
        payload: { provider: defaultProvider },
      })
      toast.info('Préparation en cours, prêt sous 30-60s.')
    } catch (err) {
      const detail =
        err && typeof err === 'object' && 'detail' in err
          ? String((err as { detail: unknown }).detail)
          : 'Préparation impossible.'
      toast.error(detail)
    }
  }

  const handleArchive = async () => {
    const confirmed = await confirm({
      title: `Archiver le produit POD ?`,
      message: `« ${variantLabel} » ne pourra plus être commandée chez le provider tant qu'elle n'est pas re-préparée. L'historique des commandes passées reste consultable.`,
      variant: 'danger',
      confirmText: 'Archiver',
    })
    if (!confirmed) return
    try {
      await archiveMutation.mutateAsync(variantId)
      toast.success('Produit POD archivé.')
    } catch (err) {
      const detail =
        err && typeof err === 'object' && 'detail' in err
          ? String((err as { detail: unknown }).detail)
          : 'Archivage impossible.'
      toast.error(detail)
    }
  }

  if (statusQuery.isLoading) {
    return (
      <span className="ui-text-muted" aria-label="Chargement statut POD">
        <Loader2 aria-hidden="true" size={14} className="pod-spinner" />
      </span>
    )
  }

  if (!podRow) {
    return (
      <Button
        variant="quiet"
        size="sm"
        onClick={handlePrepare}
        disabled={prepareMutation.isPending}
        aria-label={`Préparer ${variantLabel} pour POD`}
      >
        {prepareMutation.isPending ? (
          <Loader2 aria-hidden="true" size={14} className="pod-spinner" />
        ) : (
          <Sparkles aria-hidden="true" size={14} />
        )}
        Préparer pour POD
      </Button>
    )
  }

  return (
    <span className="pod-cell">
      <PodStatusBadge status={podRow.status} providerDisplayName={podRow.provider_display_name} />
      {canPrepare ? (
        <Button
          variant="quiet"
          size="sm"
          onClick={handlePrepare}
          disabled={prepareMutation.isPending}
          aria-label={`Réessayer la préparation POD pour ${variantLabel}`}
          title="Réessayer la préparation"
        >
          <Sparkles aria-hidden="true" size={14} />
        </Button>
      ) : null}
      {podRow.status === 'ready' || podRow.status === 'failed' ? (
        <Button
          variant="quiet"
          size="sm"
          onClick={handleArchive}
          disabled={archiveMutation.isPending}
          aria-label={`Archiver le produit POD pour ${variantLabel}`}
          title="Archiver le produit POD"
        >
          <Trash2 aria-hidden="true" size={14} />
        </Button>
      ) : null}
    </span>
  )
}
